import { Clock, MarkerPin01 } from "@untitledui/icons";
import { useEffect } from "react";
import { OVER_SPEED_LIMIT_KMH } from "../constants/tracking";
import { usePositionMetrics } from "../hooks";
import type {
	PositionMetricsTimelineItem,
	PositionMetricsTimelineItemType,
	RouteCoordinate,
} from "../types";
import {
	convertCoordinate,
	formatDurationMinutes,
	formatTime,
	splitDurationMinutes,
} from "../utils/tracking.utils";

type PositionMetricsViewProps = {
	vehicleId: number;
	initialDate?: string;
	finalDate?: string;
	lastUpdate?: string;
	onReplayReady?: (points: RouteCoordinate[]) => void;
};

const TIMELINE_DOT_COLOR: Record<PositionMetricsTimelineItemType, string> = {
	1: "bg-success-500",
	2: "bg-warning-500",
	3: "bg-blue-500",
	4: "bg-error-500",
};

const TIMELINE_EDGE_LABEL: Partial<
	Record<PositionMetricsTimelineItemType, string>
> = {
	1: "Início da Jornada",
	4: "Fim da Jornada",
};

function formatTimelineLabel(item: PositionMetricsTimelineItem): string {
	const edgeLabel = TIMELINE_EDGE_LABEL[item.type];
	if (edgeLabel) {
		return edgeLabel;
	}

	const duration = formatDurationMinutes(item.durationMinutes);
	return item.type === 2 ? `Parada de ${duration}` : `Movimento de ${duration}`;
}

function sumStoppedMinutes(timeline: PositionMetricsTimelineItem[]): number {
	return timeline
		.filter((item) => item.type === 2)
		.reduce((total, item) => total + (item.durationMinutes ?? 0), 0);
}

type MetricCardProps = {
	label: string;
	value: string;
	unit?: string;
	valueClassName?: string;
};

const MetricCard = ({
	label,
	value,
	unit,
	valueClassName,
}: MetricCardProps) => (
	<div className="rounded-lg border border-gray-200 p-4">
		<p className="text-[#667085] text-xs">{label}</p>
		<p className="mt-1 flex items-baseline gap-1">
			<span
				className={`font-semibold text-[#101828] text-display-sm ${valueClassName ?? ""}`}
			>
				{value}
			</span>
			{unit ? (
				<span className="font-normal text-[#414651] text-md">{unit}</span>
			) : null}
		</p>
	</div>
);

const LoadingState = () => (
	<div className="flex flex-1 items-center justify-center p-8">
		<p className="text-gray-500 text-sm">Carregando...</p>
	</div>
);

const ErrorState = () => (
	<div className="flex flex-1 items-center justify-center p-8 text-center">
		<p className="text-gray-500 text-sm">
			Não foi possível carregar as métricas de posição. Tente novamente.
		</p>
	</div>
);

export const PositionMetricsView = ({
	vehicleId,
	initialDate,
	finalDate,
	lastUpdate,
	onReplayReady,
}: PositionMetricsViewProps) => {
	const params =
		initialDate && finalDate ? { initialDate, finalDate } : undefined;

	const { data, isLoading, isError } = usePositionMetrics(
		vehicleId,
		params,
		vehicleId > 0 && !!params
	);

	useEffect(() => {
		if (isError) {
			onReplayReady?.([]);
			return;
		}

		if (!data) {
			return;
		}

		onReplayReady?.(
			data.positions.map((position) => ({
				lat: convertCoordinate(position.latitude),
				lng: convertCoordinate(position.longitude),
				speed: position.speed,
				dateTelemetry: position.dateTelemetry,
			}))
		);
	}, [data, isError, onReplayReady]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: only clear the replay when this view unmounts (e.g. switching to the Viagens tab), not on every onReplayReady identity change
	useEffect(
		() => () => {
			onReplayReady?.([]);
		},
		[]
	);

	if (isLoading) {
		return <LoadingState />;
	}

	if (isError) {
		return <ErrorState />;
	}

	if (!data) {
		return null;
	}

	const stoppedMinutes = sumStoppedMinutes(data.timeline);
	const stoppedDuration = splitDurationMinutes(stoppedMinutes);

	let maxSpeedValueClassName: string | undefined;
	if (data.maxSpeed > OVER_SPEED_LIMIT_KMH) {
		maxSpeedValueClassName = "text-error-primary_hover";
	}

	return (
		<div className="flex flex-col gap-4" data-slot="position-metrics-view">
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<MarkerPin01 className="size-4 shrink-0 text-gray-400" />
					<p className="font-medium text-primary text-sm">
						{data.positions.length} posições
					</p>
				</div>
				{lastUpdate ? (
					<p className="shrink-0 text-[#667085] text-xs">
						Última atualização: {lastUpdate}
					</p>
				) : null}
			</div>

			<div className="grid grid-cols-2 gap-2">
				<MetricCard
					label="Velocidade Média"
					unit="km/h"
					value={String(Math.round(data.averageSpeed))}
				/>
				<MetricCard
					label="Velocidade Máxima"
					unit="km/h"
					value={String(Math.round(data.maxSpeed))}
					valueClassName={maxSpeedValueClassName}
				/>
				<MetricCard
					label="Distância Percorrida"
					unit="km"
					value={data.totalDistanceKm.toLocaleString("pt-BR", {
						maximumFractionDigits: 1,
					})}
				/>
				<MetricCard
					label="Tempo Parado"
					unit={stoppedDuration.unit}
					value={stoppedDuration.value}
				/>
				<MetricCard
					label="Tempo Parado em %"
					unit="%"
					value={String(Math.round(data.stoppedTimePercentage))}
				/>
				<MetricCard
					label="Tempo em Movimento"
					unit="%"
					value={String(Math.round(data.movingTimePercentage))}
				/>
				<MetricCard
					label="Quantidade de Paradas"
					value={String(data.stopCount)}
				/>
				<MetricCard label="Eventos Gerados" value={String(data.totalEvents)} />
			</div>

			<div className="rounded-lg border border-gray-200 py-4 pr-1.5 pl-4">
				<p className="mb-3 font-medium text-[#344054] text-sm">
					Linha do Tempo
				</p>
				<div className="flex max-h-[320px] flex-col gap-3 overflow-y-auto pr-2">
					{data.timeline.map((item, index) => (
						<div
							className="flex items-center gap-2 text-sm"
							key={`${item.type}-${item.time}-${index}`}
						>
							<Clock className="size-4 text-gray-400" />
							<span className="text-[#667085]">{formatTime(item.time)}</span>
							<span
								className={`size-2 shrink-0 rounded-full ${TIMELINE_DOT_COLOR[item.type]}`}
							/>
							<span className="text-[#344054]">
								{formatTimelineLabel(item)}
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};
